import type { FinanceiroFiltroParams } from "@/hooks/useFinanceiro";

export type FinanceiroFiltrosDraft = {
  vencInicio: string;
  vencFim: string;
  pagInicio: string;
  pagFim: string;
  valorMin: string;
  valorMax: string;
  formaPagamento: string;
  parcelas: "todas" | "avista" | "parceladas";
  estado: string;
  cidade: string;
  status: "todos" | "pendente" | "pago" | "cancelado" | "vencido";
};

export function getInicioFimMesAtual() {
  const hoje = new Date();
  const y = hoje.getFullYear();
  const m = String(hoje.getMonth() + 1).padStart(2, "0");
  const primeiro = `${y}-${m}-01`;
  const ultimoDia = new Date(y, hoje.getMonth() + 1, 0).getDate();
  const ultimo = `${y}-${m}-${String(ultimoDia).padStart(2, "0")}`;
  return { dataInicio: primeiro, dataFim: ultimo };
}

export function getFinanceiroFiltrosDraftDefault(): FinanceiroFiltrosDraft {
  const mesAtual = getInicioFimMesAtual();
  return {
    vencInicio: mesAtual.dataInicio,
    vencFim: mesAtual.dataFim,
    pagInicio: "",
    pagFim: "",
    valorMin: "",
    valorMax: "",
    formaPagamento: "todos",
    parcelas: "todas",
    estado: "",
    cidade: "",
    status: "todos",
  };
}

const toNum = (v: string) => {
  const s = (v ?? "").trim();
  if (!s) return undefined;
  const n = Number(s.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
};

export function buildFinanceiroFiltroParams(draft: FinanceiroFiltrosDraft): FinanceiroFiltroParams {
  const vencimentoPeriodo =
    draft.vencInicio || draft.vencFim
      ? { dataInicio: draft.vencInicio || undefined, dataFim: draft.vencFim || undefined }
      : null;
  const pagamentoPeriodo =
    draft.pagInicio || draft.pagFim
      ? { dataInicio: draft.pagInicio || undefined, dataFim: draft.pagFim || undefined }
      : null;
  const valorMin = toNum(draft.valorMin);
  const valorMax = toNum(draft.valorMax);
  return {
    vencimentoPeriodo,
    pagamentoPeriodo,
    valorMin: typeof valorMin === "number" ? valorMin : null,
    valorMax: typeof valorMax === "number" ? valorMax : null,
    formaPagamento:
      draft.formaPagamento && draft.formaPagamento !== "todos" ? draft.formaPagamento : null,
    parcelas: draft.parcelas && draft.parcelas !== "todas" ? draft.parcelas : null,
    estado: draft.estado || null,
    cidade: draft.cidade || null,
    status: draft.status && draft.status !== "todos" ? draft.status : null,
  };
}

export function getPeriodosEfetivosStats(filtros?: FinanceiroFiltroParams | null) {
  const mesAtual = getInicioFimMesAtual();
  const temVenc =
    !!(filtros?.vencimentoPeriodo?.dataInicio || filtros?.vencimentoPeriodo?.dataFim);
  const temPag =
    !!(filtros?.pagamentoPeriodo?.dataInicio || filtros?.pagamentoPeriodo?.dataFim);

  const vencimento = temVenc
    ? {
        dataInicio: filtros!.vencimentoPeriodo!.dataInicio || mesAtual.dataInicio,
        dataFim: filtros!.vencimentoPeriodo!.dataFim || mesAtual.dataFim,
      }
    : mesAtual;

  const pagamento = temPag
    ? {
        dataInicio: filtros!.pagamentoPeriodo!.dataInicio || mesAtual.dataInicio,
        dataFim: filtros!.pagamentoPeriodo!.dataFim || mesAtual.dataFim,
      }
    : vencimento;

  return { vencimento, pagamento, temFiltroCustomizado: temVenc || temPag };
}

export function formatPeriodoLabel(periodo: { dataInicio: string; dataFim: string }) {
  const ini = new Date(`${periodo.dataInicio}T12:00:00`).toLocaleDateString("pt-BR");
  const fim = new Date(`${periodo.dataFim}T12:00:00`).toLocaleDateString("pt-BR");
  return `${ini} a ${fim}`;
}
